import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Typography from 'themeComponents/Typography';
import { EmojiEvents, Download, Share, CheckCircle } from '@mui/icons-material';
import { CertificateDisplay } from 'components/CertificateDisplay';
import { useCertificate } from 'hooks/useCertificate';

interface CourseCompletionScreenProps {
  courseId: string;
  courseTitle: string;
  enrollmentId: string;
  userId: string;
  userName: string;
  assessmentScore: number;
  onComplete?: () => void;
}

export const CourseCompletionScreen: React.FC<CourseCompletionScreenProps> = ({
  courseId,
  courseTitle,
  enrollmentId,
  userId,
  userName,
  assessmentScore,
  onComplete,
}) => {
  const [issuingCertificate, setIssuingCertificate] = useState(false);
  const [certificateIssued, setCertificateIssued] = useState(false);
  const [certificate, setCertificate] = useState<any>(null);
  const [error, setError] = useState('');
  const { issueCertificate } = useCertificate();
  const [openDetails, setOpenDetails] = useState(false);

  const generateCertificateNumber = () => {
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `CERT-${timestamp}-${random}`;
  };

  const handleIssueCertificate = async () => {
    setIssuingCertificate(true);
    setError('');
    try {
      const certificateNumber = generateCertificateNumber();
      const result = await issueCertificate(userId, courseId, enrollmentId, certificateNumber);

      if (result) {
        setCertificate(result);
        setCertificateIssued(true);
      } else {
        throw new Error('Failed to issue certificate');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to issue certificate');
    } finally {
      setIssuingCertificate(false);
    }
  };

  const handleDownloadCertificate = () => {
    if (certificate?.pdfUrl) {
      const link = document.createElement('a');
      link.href = certificate.pdfUrl;
      link.download = `${certificate.certificateNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleShare = () => {
    const shareText = `I've successfully completed "${courseTitle}" and earned a certificate! Certificate #${certificate?.certificateNumber}`;
    if (navigator.share) {
      navigator
        .share({
          title: 'Course Completion Certificate',
          text: shareText,
          url: window.location.href,
        })
        .catch((err) => console.log('Share error:', err));
    } else {
      navigator.clipboard.writeText(shareText);
    }
  };

  if (!certificateIssued) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 600, width: '100%', borderRadius: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            {/* Animated checkmark */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.05)' },
                    '100%': { transform: 'scale(1)' },
                  },
                }}
              >
                <CheckCircle sx={{ fontSize: 60, color: 'white' }} />
              </Box>
            </Box>

            <Typography variant="h3" sx={{ fontWeight: 'bold' }} mb={1}>
              Congratulations!
            </Typography>

            <Typography variant="h6" sx={{ color: 'text.secondary' }} mb={3}>
              You&apos;ve successfully completed
            </Typography>

            <Card
              variant="outlined"
              sx={{
                mb: 3,
                p: 2,
                background: '#f5f5f5',
                borderColor: '#667eea',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#667eea' }}>
                {courseTitle}
              </Typography>
            </Card>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Final Score
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {assessmentScore}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Status
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'green' }}>
                      Passed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Typography variant="body2" sx={{ color: 'text.secondary' }} mb={3}>
              Claim your certificate to showcase your achievement
            </Typography>

            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={issuingCertificate ? <CircularProgress size={20} color="inherit" /> : <EmojiEvents />}
              onClick={handleIssueCertificate}
              disabled={issuingCertificate}
              fullWidth
              sx={{ py: 1.5 }}
            >
              {issuingCertificate ? 'Issuing Certificate...' : 'Claim Your Certificate'}
            </Button>

            <Box mt={2}>
              <Button variant="outlined" size="small" onClick={onComplete} fullWidth>
                Skip for Now
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 700, width: '100%', borderRadius: 3, overflow: 'hidden' }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <EmojiEvents sx={{ fontSize: 32, color: '#667eea' }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Certificate Awarded!
              </Typography>
            </Box>
          </Box>

          {certificate && (
            <Box mb={4}>
              <CertificateDisplay
                certificateNumber={certificate.certificateNumber}
                courseTitle={courseTitle}
                userName={userName}
                issuedAt={certificate.issuedAt}
                pdfUrl={certificate.pdfUrl}
                imageUrl={certificate.imageUrl}
              />
            </Box>
          )}

          <Box
            sx={{
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              mb: 3,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary' }} display="block" mb={0.5}>
              Certificate Details
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Certificate #:</strong>
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  {certificate?.certificateNumber}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Issued Date:</strong>
                </Typography>
                <Typography variant="body2">
                  {new Date(certificate?.issuedAt).toLocaleDateString()}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap">
            {certificate?.pdfUrl && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Download />}
                onClick={handleDownloadCertificate}
              >
                Download PDF
              </Button>
            )}
            <Button variant="outlined" color="primary" startIcon={<Share />} onClick={handleShare}>
              Share
            </Button>
            <Button variant="outlined" onClick={onComplete} sx={{ ml: 'auto' }}>
              Continue
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Modal for full certificate preview */}
      {certificate && (
        <Dialog
          open={openDetails}
          onClose={() => setOpenDetails(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Certificate Preview</DialogTitle>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            {certificate.imageUrl && (
              <Box component="img" src={certificate.imageUrl} alt="Certificate" sx={{ maxWidth: '100%' }} />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDetails(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default CourseCompletionScreen;
